import {
  Container,
  Title,
  Grid,
  Card,
  Text,
  Group,
  Badge,
  Stack,
  Anchor,
  Image,
} from '@mantine/core';
import { CalendarIcon, ClockIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getAllPosts } from '../../utils/blogUtils';
import PublicFooter from '../../components/PublicFooter/PublicFooter';
import './Blog.css';

// Strip HTML tags from text for preview display, replacing <br/> with space
const stripHtml = (html: string): string => {
  return html
    .replace(/<br\s*\/?>/gi, ' ') // Replace <br/> or <br> with space
    .replace(/<[^>]*>/g, '') // Remove all other HTML tags
    .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
    .replace(/\s+/g, ' ') // Collapse multiple spaces into one
    .trim();
};

const Blog = () => {
  const posts = getAllPosts();

  return (
    <>
      <Container size='lg' py='xl'>
        <Stack gap='xl'>
          <div>
            <Title order={1} mb='md'>
              Resources
            </Title>
            <Text c='dimmed' size='lg'>
              Insights, updates, and best practices for modern law firms
            </Text>
          </div>

          <Grid>
            {posts.map((post) => (
              <Grid.Col key={post.id} span={{ base: 12, sm: 6, md: 4 }}>
                <Card
                  shadow='sm'
                  padding='lg'
                  radius='md'
                  withBorder
                  component={Link}
                  to={`/resources/${post.slug}`}
                  style={{
                    textDecoration: 'none',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                  }}
                >
                  <Stack gap='md' style={{ flex: 1 }}>
                    {(() => {
                      // Determine which image to use
                      let imageSrc: string | undefined;

                      if (post.podcastEpisode) {
                        // Podcast: use guest photo (circular)
                        imageSrc = post.image;
                      } else if (post.video?.type === 'explainer') {
                        // Explainer videos: use alternate image from /explainers/ folder
                        // Filename is first four words of title in kebab-case
                        // Use JPG format for both preview and social sharing (same file)
                        const titleWords = post.title
                          .replace(/<[^>]*>/g, '') // Remove HTML tags
                          .split(/\s+/)
                          .slice(0, 4)
                          .map((word) =>
                            word.toLowerCase().replace(/[^a-z0-9]/g, ''),
                          )
                          .filter((word) => word.length > 0);
                        const imageName = titleWords.join('-');
                        imageSrc = `/explainers/${imageName}.jpg`;
                      } else {
                        // Demo videos and other posts: use the image field (YouTube thumbnail for demos)
                        imageSrc = post.image;
                      }

                      if (!imageSrc) return null;

                      return post.podcastEpisode ? (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginBottom: '0.5rem',
                          }}
                        >
                          <Image
                            src={imageSrc}
                            alt={post.title}
                            width={150}
                            height={150}
                            radius='50%'
                            style={{ objectFit: 'cover' }}
                          />
                        </div>
                      ) : (
                        <Image
                          src={imageSrc}
                          alt={post.title}
                          height={200}
                          radius='md'
                          style={{ objectFit: 'cover', width: '100%' }}
                        />
                      );
                    })()}

                    <div>
                      <Badge color='blue' variant='light' mb='xs'>
                        {post.category}
                      </Badge>
                      <Title order={3} size='h4' mb='xs'>
                        {stripHtml(post.title)}
                      </Title>
                      <Text size='sm' c='dimmed' lineClamp={3}>
                        {post.excerpt}
                      </Text>
                    </div>

                    <Group gap='md' mt='auto' style={{ marginTop: 'auto' }}>
                      <Group gap={4}>
                        <CalendarIcon size={16} />
                        <Text size='xs' c='dimmed'>
                          {new Date(post.date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </Text>
                      </Group>
                      <Group gap={4}>
                        <ClockIcon size={16} />
                        <Text size='xs' c='dimmed'>
                          {post.readTime}
                        </Text>
                      </Group>
                    </Group>
                  </Stack>
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        </Stack>
      </Container>
      <PublicFooter />
    </>
  );
};

export default Blog;
